/* eslint-disable react-refresh/only-export-components */
'use client';

import { Breakpoint, ModalOwnProps, ownerDocument, useForkRef, useMediaQuery, useTheme } from '@mui/material';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { Simplify } from 'type-fest';
import { AdapterContext, RouterAdapter } from '../../utils/adapters';
import { AccessoryDrawerKey } from '../../utils/types';
import { useOverflowObserver } from '../../utils/useOverflowObserver';
import { useStringArrayMemo } from '../../utils/useStringArrayMemo';

export interface AppLayoutContextValues {
  /**
   * Current app bar variant - wide on desktop, compact on mobile
   */
  appBarVariant: 'wide' | 'compact';
  /**
   * Current app bar height. Use to adjust the layout of content below the app bar.
   */
  appBarSize: number | null;
  /**
   * True if the app sidebar is open
   */
  isSidebarOpen: boolean;
  /**
   * Toggle the app sidebar
   */
  toggleSidebar: (newValue?: boolean) => void;
  /**
   * Breakpoint used to determine when the app switches to a mobile experience. For
   * example, application features might become more touch friendly, data grids might turn
   * into lists with accordion list items.
   */
  mobileBreakpoint: Breakpoint | number;

  /**
   * Currently opened accessory drawer
   */
  openedAccessoryDrawer: AccessoryDrawerKey | null;
  /**
   * Toggle an acessory drawer
   */
  toggleAccessoryDrawer: (drawer: AccessoryDrawerKey, newValue?: boolean) => void;
  /**
   * Close all accessory drawers
   */
  closeAccessoryDrawers: () => void;

  // -----

  /**
   * Consumed by <AppSidebar>, which typically becomes a temporary drawer on mobile.
   * @internal
   */
  sidebarVariant: 'temporary' | 'persistent';
  /**
   * True if the right app sidebar is open
   */
  isRightSidebarOpen: boolean;
  /**
   * Toggle the right app sidebar
   */
  toggleRightSidebar: (newValue?: boolean) => void;
  /**
   * Consumed by <AppSidebar anchor="right">, which typically becomes a temporary drawer on mobile.
   * @internal
   */
  rightSidebarVariant: 'temporary' | 'persistent';
  /**
   * Ref for resize observer
   * @internal
   */
  internalAppBarContainerRef: React.RefCallback<HTMLDivElement | null>;
  /**
   * Ref for resize observer
   * @internal
   */
  internalAppBarContentNodeRef: React.MutableRefObject<HTMLDivElement | null>;
  /**
   * Container for AppSidebar and AccessoryDrawer - used in stories
   * @internal
   */
  drawerContainer: ModalOwnProps['container'];
  /**
   * @internal
   */
  features: Array<'desktopOnly' | 'persistentSidebar' | 'persistentRightSidebar'>;
}

export const AppLayoutContext = createContext<AppLayoutContextValues | undefined>(undefined);

/**
 * Using the current app bar height in React components:
 *
 * ```tsx
 * const Component = () => {
 *   const { appBarSize } = useAppLayout()
 *   return <div style={{ position: 'sticky', top: appBarSize }} />
 * }
 * ```
 *
 * Or with CSS variables:
 *
 * ```tsx
 * const Component = () => {
 *   const { appBarSize } = useAppLayout()
 *   return <div style={{ '--app-bar-height': appBarSize, top: 'var(--app-bar-height)' }} />
 * }
 * ```
 */
export const useAppLayout = (): AppLayoutContextValues => {
  const context = useContext(AppLayoutContext);
  if (!context) {
    throw new Error('useAppLayout must be used within a AppLayoutProvider');
  }
  return context;
};

export interface AppLayoutProviderOwnProps {
  /**
   * Router adapter
   */
  adapter: RouterAdapter | undefined;
  /**
   * desktopOnly*
   *
   * Disable switching to a compact app bar variant in mobile viewports. Useful for
   * desktop-only apps.
   *
   * persistentSidebar*
   *
   * Use a persistent `<AppSidebar>` shown at all times, as opposed the default app
   * sidebar shown only on small viewports.
   *
   * persistentRightSidebar*
   *
   * Use a persistent `<AppRightSidebar>` shown at all times, as opposed to the default
   * right sidebar shown only on mobile viewports.
   */
  features?: Simplify<AppLayoutContextValues['features']>;
  /**
   * Breakpoint used to determine when the app switches to a mobile experience. For
   * example, application features might become more touch friendly, data grids might turn
   * into lists with accordion list items.
   *
   * It is medium (md) by default.
   *
   * The navigation switches between mobile view (drawer) and desktop view (full width) by
   * detecting app bar content clipping. For server-side rendered apps, we default to this
   * breakpoint.
   */
  mobileBreakpoint?: Breakpoint | number;
  /**
   * Contents
   */
  children?: ReactNode;
  /**
   * Initial value for 'isSidebarOpen'
   */
  initialSidebarOpen?: boolean;
  /**
   * Initial value for 'isRightSidebarOpen'
   */
  initialRightSidebarOpen?: boolean;
  /**
   * Initial value for 'openedAccessoryDrawer'
   */
  initialAccessoryDrawer?: AccessoryDrawerKey;
  /**
   * Container for `<AppSidebar>` and `<AccessoryDrawer>`
   * @internal
   */
  drawerContainer?: ModalOwnProps['container'];
}

const emptyArray = [] as [];

/**
 * Wrap your app with this provider and pass a router adapter.
 *
 * The provider coordinates state between `<AppBarLayout>`, `<AppSidebar>` and
 * `<AccessoryDrawer>` components so that they work nicely together across different
 * viewports and configurations.
 *
 * @example
 * ```tsx
 * <Router>
 *  <AppLayoutProvider adapter={tanstackRouterAdapter}>
 *    <DashboardLayout>
 *      {children}
 *    </DashboardLayout>
 *  </AppLayoutProvider>
 * </Router>
 * ```
 *
 * Internally, the provider:
 * - decides when to switch between compact and wide app bar (mobile and desktop) based
 *   on CSS breakpoints and navigation width
 * - controls the app sidebar state (open / closed) and variant (temporary / persistent)
 * - observes app bar height so that it can be used in other layout components
 * - manages the state of accessory drawers (only one can be open at a time)
 * - tells `<NavigationItem>` components how to render a router link
 */
export const AppLayoutProvider = ({
  adapter,
  features: featuresProp = emptyArray,
  mobileBreakpoint = 'md',
  children,
  initialSidebarOpen,
  initialRightSidebarOpen,
  initialAccessoryDrawer,
  drawerContainer: drawerContainerProp,
}: AppLayoutProviderOwnProps) => {
  const theme = useTheme();
  const hasMobileViewport = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));

  const features = useStringArrayMemo(featuresProp);

  // Never show the compact app bar
  const desktopOnly = features.includes('desktopOnly');

  // Show the app sidebar at all times, as opposed to only when the app bar is compact
  // (default behaviour)
  const hasPersistentSidebar = features.includes('persistentSidebar');

  // Check if app bar content is overflowing (whether it fits into its container).
  // If it does, we'll switch to the compact app bar earlier than mobile breakpoint.
  // SSR is not supported - useOverflowState() relies on resize observer
  const {
    isContentOverflowing,
    containerNodeRef: overflowObserverContainerRef,
    contentNodeRef: internalAppBarContentNodeRef,
  } = useOverflowObserver();

  // In small viewports, app bar becomes more compact and shows a sidebar toggle button
  const appBarVariant =
    (isContentOverflowing || hasMobileViewport) && !desktopOnly ? ('compact' as const) : ('wide' as const);

  // If an app has a sidebar, in small viewports, the sidebar always becomes a temporary drawer
  // Assume persistent sidebar (<AppSidebar>) by default
  const sidebarVariant = isContentOverflowing || hasMobileViewport ? ('temporary' as const) : ('persistent' as const);

  // App bar height may be used e.g. by sticky elements in ScrollableBodyLayout, or a
  // secondary toolbar - see stories for examples.
  //
  // We don't want developers to hardcode app bar height in their apps.
  //
  // Why a resize observer if we know the height beforehand? We don't - subpixel rendering
  // may cause the computed height to be different than the one defined in CSS. Other UI
  // components will rely on this height to be correct and precise. This resize detector
  // will also fire when window.devicePixelRatio changes (e.g. when moving the app from
  // one monitor to another) which may change the true app bar height.
  const { height, ref: resizeDetectorRef } = useResizeDetector({
    handleWidth: false,
    handleHeight: true,
    refreshMode: 'throttle',
    refreshRate: 166,
    observerOptions: { box: 'border-box' },
  });

  // Even if apps use a persistent sidebar, it will become a temporary drawer on mobile.
  // We use a separate state to track if the two different variants are open, even if it's
  // the same component. E.g. if we open a mobile sidebar, it doesn't mean that the desktop
  // sidebar should be open too when we resize the browser window.
  const [isTemporarySidebarOpen, setTemporarySidebarOpen] = useState(false);
  // App developers may decide to make the persistent sidebar togglable.
  const [isPersistentSidebarOpen, setPersistentSidebarOpen] = useState(initialSidebarOpen ?? true);

  const isSidebarOpen = (() => {
    if (sidebarVariant === 'temporary') {
      return isTemporarySidebarOpen;
    } else if (!hasPersistentSidebar && appBarVariant !== 'compact') {
      // DefaultAppSidebar (defined in AppBarLayout) is only present if the app bar is
      // compact. If the app bar is wide, we will leave this sidebar closed.
      return false;
    } else {
      return isPersistentSidebarOpen;
    }
  })();

  const toggleSidebar = useCallback(
    (newValue?: boolean) => {
      function setStateAction(previous: boolean) {
        if (typeof newValue === 'boolean') {
          return newValue;
        } else {
          return !previous;
        }
      }
      if (sidebarVariant === 'temporary') {
        setTemporarySidebarOpen(setStateAction);
      } else {
        setPersistentSidebarOpen(setStateAction);
      }
    },
    [sidebarVariant],
  );

  // Close temporary sidebar when app bar variant becomes 'wide'
  useEffect(() => {
    if (sidebarVariant !== 'temporary') {
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      setTemporarySidebarOpen(false);
    }
  }, [sidebarVariant]);

  // --- RIGHT SIDEBAR ---
  const hasPersistentRightSidebar = features.includes('persistentRightSidebar');

  const [isTemporaryRightSidebarOpen, setTemporaryRightSidebarOpen] = useState(false);
  const [isPersistentRightSidebarOpen, setPersistentRightSidebarOpen] = useState(initialRightSidebarOpen ?? true);

  const isRightSidebarOpen = (() => {
    if (sidebarVariant === 'temporary') return isTemporaryRightSidebarOpen;
    if (!hasPersistentRightSidebar) return false;
    return isPersistentRightSidebarOpen;
  })();

  const toggleRightSidebar = useCallback(
    (newValue?: boolean) => {
      function setStateAction(previous: boolean) {
        if (typeof newValue === 'boolean') {
          return newValue;
        } else {
          return !previous;
        }
      }
      if (sidebarVariant === 'temporary') {
        setTemporaryRightSidebarOpen(setStateAction);
      } else {
        setPersistentRightSidebarOpen(setStateAction);
      }
    },
    [sidebarVariant],
  );

  // Close temporary right sidebar when layout switches to desktop
  useEffect(() => {
    if (sidebarVariant !== 'temporary') {
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      setTemporaryRightSidebarOpen(false);
    }
  }, [sidebarVariant]);

  const [openedAccessoryDrawer, setOpenedAccessoryDrawer] = useState<AccessoryDrawerKey | null>(
    initialAccessoryDrawer ?? null,
  );

  const toggleAccessoryDrawer = useCallback(
    (drawer: AccessoryDrawerKey, newValue?: boolean) => {
      setOpenedAccessoryDrawer((previous) => {
        if (typeof newValue === 'boolean') {
          return newValue ? drawer : previous === drawer ? null : previous;
        } else {
          return previous === drawer ? null : drawer;
        }
      });
    },
    [setOpenedAccessoryDrawer],
  );

  const closeAccessoryDrawers = useCallback(() => {
    setOpenedAccessoryDrawer(null);
  }, [setOpenedAccessoryDrawer]);

  const internalAppBarContainerRef = useForkRef<HTMLDivElement | null>(
    resizeDetectorRef,
    overflowObserverContainerRef,
  )!;

  // Not allowed to change over time. Defaults to app bar owner document.
  const [initialDrawerContainer] = useState(
    () => drawerContainerProp ?? (() => ownerDocument(resizeDetectorRef?.current?.())?.body),
  );

  const contextValue = useMemo(
    () => ({
      appBarVariant,
      appBarSize: height ?? null,
      isSidebarOpen,
      toggleSidebar,
      features,
      sidebarVariant,
      isRightSidebarOpen,
      toggleRightSidebar,
      rightSidebarVariant: sidebarVariant,
      openedAccessoryDrawer,
      toggleAccessoryDrawer,
      closeAccessoryDrawers,
      internalAppBarContainerRef,
      internalAppBarContentNodeRef,
      drawerContainer: initialDrawerContainer,
      mobileBreakpoint,
    }),
    [
      appBarVariant,
      height,
      isSidebarOpen,
      toggleSidebar,
      features,
      sidebarVariant,
      isRightSidebarOpen,
      toggleRightSidebar,
      openedAccessoryDrawer,
      toggleAccessoryDrawer,
      closeAccessoryDrawers,
      internalAppBarContainerRef,
      internalAppBarContentNodeRef,
      initialDrawerContainer,
      mobileBreakpoint,
    ],
  );

  return (
    // eslint-disable-next-line react/no-context-provider
    <AppLayoutContext.Provider value={contextValue}>
      {/* eslint-disable-next-line react/no-context-provider */}
      {adapter ? <AdapterContext.Provider value={adapter}>{children}</AdapterContext.Provider> : children}
    </AppLayoutContext.Provider>
  );
};
